/**
 * Notification Provider - Hybrid Notification System
 * 
 * ORCHESTRATES:
 * 1. Pusher (Realtime In-App) - Tab OPEN
 * 2. Web Push (Browser/OS) - Tab CLOSED
 * 
 * This component manages both notification systems and provides
 * a unified interface for the rest of the app.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { usePusher } from '../hooks/usePusher';
import { useWebPush } from '../hooks/useWebPush';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';
import { storage } from '@/utils/storage';
import { notificationAPI } from '@/lib/api';

interface Notification {
    id: number;
    title: string;
    message: string;
    created_at: string;
    deleted_at: string | null;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    isLoading: boolean;

    // Pusher status
    isPusherConnected: boolean;
    isPusherAvailable: boolean;

    // Web Push status
    isWebPushSupported: boolean;
    isWebPushSubscribed: boolean;
    isWebPushAvailable: boolean;

    // Actions
    fetchNotifications: () => Promise<void>;
    markAsRead: (id: number) => Promise<void>;
    deleteNotification: (id: number) => Promise<void>;
    requestPushPermission: () => Promise<boolean>;
    subscribeToPush: () => Promise<boolean>;
    unsubscribeFromPush: () => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within NotificationProvider');
    }
    return context;
};

interface NotificationProviderProps {
    children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const { t } = useTranslation();
    const { user, authToken } = useAuth();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ═══════════════════════════════════════════════════════
    // PUSHER - Realtime In-App Notifications
    // ═══════════════════════════════════════════════════════

    const handleNotificationCreated = useCallback((notification: Notification) => {
        // Add to notifications list
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);

        // Show toast popup
        toast.success(notification.title, {
            description: notification.message,
            duration: 5000,
        });
    }, []);

    const handleNotificationRead = useCallback((notificationId: number) => {
        // Update local state
        setNotifications(prev =>
            prev.map(n => n.id === notificationId ? { ...n, deleted_at: new Date().toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    const handleNotificationDeleted = useCallback((notificationId: number) => {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        setUnreadCount(prev => Math.max(0, prev - 1));
    }, []);

    const {
        isConnected: isPusherConnected,
        isAvailable: isPusherAvailable
    } = usePusher(
        handleNotificationCreated,
        handleNotificationRead,
        handleNotificationDeleted
    );

    // Keep Redis presence warm while Pusher is connected (multi-worker online detection).
    // Interval must stay under PRESENCE_TTL_SECONDS (120s). Cleanup stops beats on disconnect.
    useEffect(() => {
        if (!user || !authToken || !isPusherConnected) return;

        let cancelled = false;
        const beat = async () => {
            if (cancelled) return;
            try {
                await notificationAPI.presenceHeartbeat();
            } catch {
                // Presence is best-effort; offline email path still works.
            }
        };

        void beat();
        const id = window.setInterval(() => {
            void beat();
        }, 60_000);

        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [user, authToken, isPusherConnected]);

    // ═══════════════════════════════════════════════════════
    // WEB PUSH - Browser/OS Notifications
    // ═══════════════════════════════════════════════════════

    const {
        isSupported: isWebPushSupported,
        isSubscribed: isWebPushSubscribed,
        isAvailable: isWebPushAvailable,
        requestPermission: requestPushPermission,
        subscribe: subscribeToPush,
        unsubscribe: unsubscribeFromPush
    } = useWebPush();


    const fetchNotifications = useCallback(async (showErrorToast = true) => {
        if (!user || !authToken) return;

        setIsLoading(true);
        try {
            const data = await notificationAPI.getNotifications();
            setNotifications(data.notifications || []);
            setUnreadCount(data.unread_count || 0);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            if (showErrorToast) {
                toast.error(t('notifications.failed_to_load'));
            }
        } finally {
            setIsLoading(false);
        }
    }, [user, authToken, t]);

    /**
     * Mark notification as read
     */
    const markAsRead = useCallback(async (id: number) => {
        if (!authToken) return;

        // Optimistically update local UI immediately
        handleNotificationRead(id);

        try {
            await notificationAPI.markAsRead(id);
        } catch (err) {
            console.error('Error marking notification as read:', err);
            toast.error(t('notifications.failed_to_mark_read'));
            fetchNotifications(false);
        }
    }, [authToken, handleNotificationRead, fetchNotifications, t]);

    /**
     * Delete notification
     */
    const deleteNotification = useCallback(async (id: number) => {
        if (!authToken) return;

        // Optimistically update local UI immediately
        handleNotificationDeleted(id);

        try {
            await notificationAPI.deleteNotification(id);
        } catch (err) {
            console.error('Error deleting notification:', err);
            toast.error(t('notifications.failed_to_delete'));
            fetchNotifications(false);
        }
    }, [authToken, handleNotificationDeleted, fetchNotifications, t]);

    // Fetch notifications on mount/login
    // On initial load, suppress the error toast and retry silently once after 3s
    // to avoid false "Failed to load" errors from auth token race conditions or backend cold start.
    useEffect(() => {
        if (user && authToken) {
            // Clear any pending retry from a previous auth state
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }

            // First attempt: silent (no error toast)
            fetchNotifications(false).catch(() => {
                // If it fails silently, schedule one retry after 3s with the error toast
                retryTimerRef.current = setTimeout(() => {
                    fetchNotifications(true);
                    retryTimerRef.current = null;
                }, 3000);
            });
        }

        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [user, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-sync or Auto-subscribe to push notifications
    useEffect(() => {
        if (!user || !authToken || !isWebPushSupported || !isWebPushAvailable) {
            return;
        }

        // Check if user has enabled web push notifications in their settings
        const userWantsPushNotifications = user?.json_data?.notification_settings?.web_push === true;

        if (!userWantsPushNotifications) {
            return;
        }

        // If we have a subscription in the browser but we haven't confirmed it with the backend this session
        const hasSyncedThisSession = storage.session.getItem('push_auto_synced');

        if (!hasSyncedThisSession) {
            const timer = setTimeout(async () => {
                try {
                    // This will now handle both NEW subscriptions and RE-SYNCING existing ones
                    const success = await subscribeToPush();

                    if (success) {
                        storage.session.setItem('push_auto_synced', 'true');
                    } else {
                        console.warn('⚠️ Push sync did not return success');
                    }
                } catch (err) {
                    console.error('❌ Push sync failed:', err);
                }
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [user, authToken, isWebPushSupported, isWebPushAvailable, subscribeToPush]);

    const value: NotificationContextType = {
        notifications,
        unreadCount,
        isLoading,

        // Pusher status
        isPusherConnected,
        isPusherAvailable,

        // Web Push status
        isWebPushSupported,
        isWebPushSubscribed,
        isWebPushAvailable,

        // Actions
        fetchNotifications,
        markAsRead,
        deleteNotification,
        requestPushPermission,
        subscribeToPush,
        unsubscribeFromPush
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};
